import { URLS } from "@app/shared";
import irAppImage from "@assets/images/investor-relations/ir-app-phones.webp";
import appsQRCode from "@assets/images/investors/SENAAT_IR_QR_CODE.png";
import { Link } from "@design-system/components";
import { trans } from "@mongez/localization";
import { ArrowRight } from "lucide-react";
import { IrAppStoreLinks } from "./IrAppStoreBadges";

const subscribeHeadingId = "subscribe-financial-updates-heading";
const appHeadingId = "investor-relations-app-heading";

/**
 * Decorative envelope — no approved illustration was supplied, so this matches
 * the one drawn in the design: a white envelope with an orange front panel and
 * a results sheet tucked inside.
 */
function SubscribeArtwork() {
  return (
    <svg
      viewBox="0 0 200 166"
      className="h-auto w-40 shrink-0 sm:w-48 xl:w-56"
      fill="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="subscribe-flap" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FE4F02" />
          <stop offset="100%" stopColor="#F37E31" />
        </linearGradient>
      </defs>

      {/* letter, tucked behind the envelope's front panel */}
      <rect
        x="52"
        y="6"
        width="96"
        height="88"
        rx="5"
        fill="#FFFFFF"
        stroke="#CBD8EA"
        strokeWidth="1.5"
      />
      {/* Only the top ~56 units of the letter clear the envelope body. */}
      <path
        d="M68 22h64M68 33h64"
        stroke="#C7D6EA"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path
        d="M68 56l17-13 15 7 16-16 16 11"
        stroke="#0046BB"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* envelope body */}
      <path d="M20 62h160v92H20z" fill="#F4F7FC" />
      {/* side panels, folding back towards the centre */}
      <path d="M20 62l80 58-80 34z" fill="#E2E9F4" />
      <path d="M180 62l-80 58 80 34z" fill="#E2E9F4" />
      {/* front panel */}
      <path d="M20 154h160l-80-58z" fill="url(#subscribe-flap)" />

      <circle cx="176" cy="42" r="18" fill="#FE4F02" />
      <path
        d="M167 42h18M176 33v18"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Sections 12 and 13 of the approved IR content sheet, merged into the single
 * split band shown in the design — the app column runs wider because it
 * carries the most content.
 */
export function InvestorEngagement() {
  return (
    <section className="border-y border-white/15 bg-ir-band">
      <div className="container grid gap-10 py-14 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] xl:gap-0 xl:py-16">
        {/* Subscribe to Financial Updates */}
        <div
          aria-labelledby={subscribeHeadingId}
          role="region"
          className="flex flex-col items-center gap-6 text-center sm:flex-row sm:justify-between sm:text-start xl:pe-12"
        >
          <div className="flex flex-col items-center gap-5 sm:items-start">
            <h2
              id={subscribeHeadingId}
              className="text-2xl font-bold leading-tight text-white sm:text-3xl xl:text-[2rem]"
            >
              <span className="block">
                {trans("subscribeToFinancialUpdates.line1")}
              </span>
              <span className="block">
                {trans("subscribeToFinancialUpdates.line2")}
              </span>
            </h2>

            <p className="max-w-sm text-sm leading-relaxed text-white/70">
              {trans("subscribeToFinancialUpdates.description")}
            </p>

            <Link
              to={URLS.investor.emailSubscriptionCenter}
              aria-label={trans("subscribeToFinancialUpdates.ariaLabel")}
              className="inline-flex items-center gap-2.5 rounded-full bg-bright-orange px-7 py-3 text-sm font-bold uppercase tracking-[0.08em] text-white transition-[background-color,transform] duration-300 hover:bg-steel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white active:scale-[0.98]"
            >
              {trans("subscribeToFinancialUpdates.cta")}
              <ArrowRight
                className="size-5 shrink-0 stroke-[2.25] rtl:rotate-180"
                aria-hidden
              />
            </Link>
          </div>

          <SubscribeArtwork />
        </div>

        {/* SENAAT Investor Relations App */}
        <div
          aria-labelledby={appHeadingId}
          role="region"
          className="flex flex-col items-center gap-8 border-t border-white/10 pt-10 text-center sm:flex-row sm:justify-between sm:text-start xl:border-s xl:border-t-0 xl:ps-12 xl:pt-0"
        >
          <div className="flex flex-col items-center gap-5 sm:items-start">
            <h2
              id={appHeadingId}
              className="text-2xl font-bold leading-tight text-white sm:text-3xl xl:text-[2rem]"
            >
              {trans("irAppBanner.title")}
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-white/70">
              {trans("irAppBanner.description")}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 sm:justify-start">
              <img
                src={appsQRCode}
                alt=""
                width={200}
                height={200}
                loading="lazy"
                className="size-20 rounded-md bg-white p-1 sm:size-[5.5rem]"
              />
              <IrAppStoreLinks />
            </div>
          </div>

          <img
            src={irAppImage}
            alt=""
            width={900}
            height={874}
            loading="lazy"
            className="h-auto w-56 shrink-0 object-contain drop-shadow-xl sm:w-72 lg:w-80 xl:w-[22rem]"
          />
        </div>
      </div>
    </section>
  );
}
